import React from 'react';
import { Property } from '../../../types';
import { SearchIcon } from '../../../components/Icons';
import { SpreadsheetFilters } from '../../../hooks/useAdminSpreadsheet';
import { OperationTableRow } from './operation/OperationTableRow';

interface OperationSpreadsheetTableProps {
    properties: Property[];
    allowedOwners: string[];
    filters: SpreadsheetFilters;
    updateFilters: (f: Partial<SpreadsheetFilters>) => void;
    localEdits: Record<string, Partial<Property>>;
    savingIds: Set<string>;
    onLocalEdit: (id: string, field: keyof Property, value: any) => void;
    onSave: (id: string) => void;
    codeInput: string;
    setCodeInput: (v: string) => void;
    brpInput: string;
    setBrpInput: (v: string) => void;
    onOpenScheduleEditor?: (id: string, initialValue: string) => void;
}

export const OperationSpreadsheetTable: React.FC<OperationSpreadsheetTableProps> = ({
    properties, allowedOwners, filters, updateFilters, localEdits, savingIds, 
    onLocalEdit, onSave, codeInput, setCodeInput, brpInput, setBrpInput, onOpenScheduleEditor
}) => {
    const headerClass = "px-4 py-4 text-[10px] font-black text-gray-500 uppercase tracking-widest min-w-[180px]";
    
    return (
        <div className="overflow-x-auto custom-scrollbar">
            <table className="min-w-full text-left border-separate border-spacing-0">
                <thead>
                    <tr className="bg-gray-100/80 border-b border-gray-200">
                        <th className="px-4 py-4 min-w-[150px] sticky left-0 bg-gray-100 z-10 border-r border-gray-200">
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Código</span>
                                <div className="relative">
                                    <input 
                                        type="text"
                                        value={codeInput}
                                        onChange={(e) => setCodeInput(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && updateFilters({ propertyCode: codeInput })}
                                        placeholder="Buscar ID..."
                                        className="text-[10px] font-bold border-gray-200 rounded-lg p-1 w-full pr-6 outline-none focus:ring-1 focus:ring-sky-500"
                                    />
                                    <SearchIcon className="absolute right-1.5 top-1.5 w-3 h-3 text-gray-400" />
                                </div>
                            </div>
                        </th>
                        <th className="px-4 py-4 min-w-[140px]">
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Estado</span>
                                <select 
                                    value={filters.status[0] || ""}
                                    onChange={(e) => updateFilters({ status: e.target.value ? [e.target.value] : [] })}
                                    className="text-[10px] font-bold border-gray-200 rounded-lg p-1 w-full outline-none focus:ring-1 focus:ring-sky-500"
                                >
                                    <option value="">TODOS</option>
                                    <option value="Activo">ACTIVOS</option>
                                    <option value="Eliminado">ELIMINADOS</option>
                                    <option value="Actualizado">ACTUALIZADOS</option>
                                </select>
                            </div>
                        </th>
                        <th className={headerClass}>Nuevo Código</th>
                        <th className="px-4 py-4 min-w-[200px]">
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Owner (183150)</span>
                                <select 
                                    value={filters.owners[0] || ""}
                                    onChange={(e) => updateFilters({ owners: e.target.value ? [e.target.value] : [] })}
                                    className="text-[10px] font-bold border-gray-200 rounded-lg p-1 w-full outline-none focus:ring-1 focus:ring-sky-500"
                                >
                                    <option value="">TODOS</option>
                                    {allowedOwners.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </div>
                        </th>
                        <th className="px-4 py-4 min-w-[150px]">
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Código BRP</span>
                                <div className="relative">
                                    <input 
                                        type="text"
                                        value={brpInput}
                                        onChange={(e) => setBrpInput(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && updateFilters({ brpCode: brpInput })}
                                        placeholder="Buscar..."
                                        className="text-[10px] font-bold border-gray-200 rounded-lg p-1 w-full pr-6 outline-none focus:ring-1 focus:ring-sky-500"
                                    />
                                    <SearchIcon className="absolute right-1.5 top-1.5 w-3 h-3 text-gray-400" />
                                </div>
                            </div>
                        </th>
                        <th className={headerClass}>Matrícula</th>
                        <th className={headerClass}>Predial</th>
                        <th className={headerClass}>Piso</th>
                        <th className={headerClass}>Administración</th>
                        <th className={headerClass}>Horarios Visita</th>
                        <th className={headerClass}>Nombre Cliente</th>
                        <th className={headerClass}>Celular Cliente</th>
                        <th className={headerClass}>Correo Cliente</th>
                        <th className={headerClass}>Encargado</th>
                        <th className={headerClass}>Tel. Encargado</th>
                        <th className="px-4 py-4 text-[10px] font-black text-gray-500 uppercase tracking-widest min-w-[300px]">Observaciones</th>
                        <th className="px-4 py-4 text-[10px] font-black text-gray-500 uppercase tracking-widest min-w-[100px] text-center sticky right-0 bg-gray-100 z-10 border-l border-gray-200">Acción</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {properties.map(p => (
                        <OperationTableRow 
                            key={p.id}
                            property={p}
                            allowedOwners={allowedOwners}
                            localEdits={localEdits[p.id] || {}}
                            isSaving={savingIds.has(p.id)}
                            onLocalEdit={(f, v) => onLocalEdit(p.id, f, v)}
                            onSave={() => onSave(p.id)}
                            onOpenScheduleEditor={onOpenScheduleEditor}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};